import { type ComponentType } from 'react'

import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'
import { type CrossClientAction, type CrossClientQueryResult } from '../../../models/CrossClient'
import { type ActionTracking } from './useActionTracking'
import { WealthboxTagsActionModal } from './modals/WealthboxTagsActionModal'
import { WealthboxNoteActionModal } from './modals/WealthboxNoteActionModal'
import { WealthboxTaskActionModal } from './modals/WealthboxTaskActionModal'
import { WealthboxWorkflowActionModal } from './modals/WealthboxWorkflowActionModal'
import { RedtailNoteActionModal } from './modals/RedtailNoteActionModal'
import { RedtailCategoryActionModal } from './modals/RedtailCategoryActionModal'
import { RedtailTaskActionModal } from './modals/RedtailTaskActionModal'
import { AdvisorEngineNoteActionModal } from './modals/AdvisorEngineNoteActionModal'
import { AdvisorEngineTaskActionModal } from './modals/AdvisorEngineTaskActionModal'
import { createSalesforceStyleNoteModal, createSalesforceStyleTagsModal, createSalesforceStyleTaskModal } from './modals/salesforceStyleActionModals'

// Salesforce and the CRMs that overlay it share one data model, so they reuse the same note/task modals.
const SALESFORCE_FAMILY_SERVICE_IDS = [
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.XLR8,
] as const

type SalesforceFamilyServiceId = (typeof SALESFORCE_FAMILY_SERVICE_IDS)[number]
type SalesforceFamilyActionKind =
  | `${SalesforceFamilyServiceId}-note`
  | `${SalesforceFamilyServiceId}-task`
  | `${SalesforceFamilyServiceId}-UserDefinedLabelAssignment`

export type CrmActionKind =
  | 'wealthbox-tag'
  | 'wealthbox-note'
  | 'wealthbox-task'
  | 'wealthbox-workflow'
  | 'redtail-note'
  | 'redtail-category'
  | 'redtail-task'
  | 'advisorengine-note'
  | 'advisorengine-task'
  | SalesforceFamilyActionKind

export interface CrmActionModalProps {
  open: boolean
  onClose: () => void
  resolveResults: () => Promise<CrossClientQueryResult[]>
  tracking: ActionTracking
}

export interface CrmActionDescriptor {
  serviceId: IntegrationServiceId
  label: string
  icon: string
  inProgressLabel: string
  completedLabel: string
  Modal: ComponentType<CrmActionModalProps>
}

const salesforceStyleNoteDescriptor = (serviceId: SalesforceFamilyServiceId): CrmActionDescriptor => {
  const crmName = getIntegrationDisplayName(serviceId)
  return {
    serviceId,
    label: 'Add note',
    icon: 'bi-journal-text',
    inProgressLabel: `Adding note in ${crmName}`,
    completedLabel: `Added note in ${crmName}`,
    Modal: createSalesforceStyleNoteModal(serviceId),
  }
}

const salesforceStyleTaskDescriptor = (serviceId: SalesforceFamilyServiceId): CrmActionDescriptor => {
  const crmName = getIntegrationDisplayName(serviceId)
  return {
    serviceId,
    label: 'Create task',
    icon: 'bi-list-task',
    inProgressLabel: `Adding task in ${crmName}`,
    completedLabel: `Added task in ${crmName}`,
    Modal: createSalesforceStyleTaskModal(serviceId),
  }
}

const salesforceStyleTagsDescriptor = (serviceId: SalesforceFamilyServiceId): CrmActionDescriptor => {
  const crmName = getIntegrationDisplayName(serviceId)
  return {
    serviceId,
    label: 'Assign labels',
    icon: 'bi-tag',
    inProgressLabel: `Assigning label in ${crmName}`,
    completedLabel: `Assigned label in ${crmName}`,
    Modal: createSalesforceStyleTagsModal(serviceId),
  }
}

const salesforceFamilyRegistry = SALESFORCE_FAMILY_SERVICE_IDS.reduce(
  (acc, serviceId) => {
    acc[`${serviceId}-note`] = salesforceStyleNoteDescriptor(serviceId)
    acc[`${serviceId}-task`] = salesforceStyleTaskDescriptor(serviceId)
    acc[`${serviceId}-UserDefinedLabelAssignment`] = salesforceStyleTagsDescriptor(serviceId)
    return acc
  },
  {} as Record<SalesforceFamilyActionKind, CrmActionDescriptor>,
)

export const CRM_ACTION_REGISTRY: Record<CrmActionKind, CrmActionDescriptor> = {
  'wealthbox-tag': {
    serviceId: IntegrationServiceId.WEALTHBOX,
    label: 'Assign tags',
    icon: 'bi-tag',
    inProgressLabel: 'Assigning tag in Wealthbox',
    completedLabel: 'Assigned tag in Wealthbox',
    Modal: WealthboxTagsActionModal,
  },
  'wealthbox-note': {
    serviceId: IntegrationServiceId.WEALTHBOX,
    label: 'Add note',
    icon: 'bi-journal-text',
    inProgressLabel: 'Adding note in Wealthbox',
    completedLabel: 'Added note in Wealthbox',
    Modal: WealthboxNoteActionModal,
  },
  'wealthbox-task': {
    serviceId: IntegrationServiceId.WEALTHBOX,
    label: 'Create task',
    icon: 'bi-list-task',
    inProgressLabel: 'Adding task in Wealthbox',
    completedLabel: 'Added task in Wealthbox',
    Modal: WealthboxTaskActionModal,
  },
  'wealthbox-workflow': {
    serviceId: IntegrationServiceId.WEALTHBOX,
    label: 'Start workflow',
    icon: 'bi-diagram-3',
    inProgressLabel: 'Starting workflow in Wealthbox',
    completedLabel: 'Started workflow in Wealthbox',
    Modal: WealthboxWorkflowActionModal,
  },
  'redtail-note': {
    serviceId: IntegrationServiceId.REDTAIL,
    label: 'Add note',
    icon: 'bi-journal-text',
    inProgressLabel: 'Adding note in Redtail',
    completedLabel: 'Added note in Redtail',
    Modal: RedtailNoteActionModal,
  },
  'redtail-category': {
    serviceId: IntegrationServiceId.REDTAIL,
    label: 'Assign category',
    icon: 'bi-tag',
    inProgressLabel: 'Assigning category in Redtail',
    completedLabel: 'Assigned category in Redtail',
    Modal: RedtailCategoryActionModal,
  },
  'redtail-task': {
    serviceId: IntegrationServiceId.REDTAIL,
    label: 'Create activity',
    icon: 'bi-list-task',
    inProgressLabel: 'Adding activity in Redtail',
    completedLabel: 'Added activity in Redtail',
    Modal: RedtailTaskActionModal,
  },
  'advisorengine-note': {
    serviceId: IntegrationServiceId.ADVISOR_ENGINE,
    label: 'Add note',
    icon: 'bi-journal-text',
    inProgressLabel: 'Adding note in AdvisorEngine',
    completedLabel: 'Added note in AdvisorEngine',
    Modal: AdvisorEngineNoteActionModal,
  },
  'advisorengine-task': {
    serviceId: IntegrationServiceId.ADVISOR_ENGINE,
    label: 'Create task',
    icon: 'bi-list-task',
    inProgressLabel: 'Adding task in AdvisorEngine',
    completedLabel: 'Added task in AdvisorEngine',
    Modal: AdvisorEngineTaskActionModal,
  },
  ...salesforceFamilyRegistry,
}

// Keyed by `${connector}.${discriminator}`. For `entity`-type actions (contact updates) the backend
// reports `actionType` as `entity`, so the meaningful discriminator is `details.entity_type`
// (`set_tags` for Wealthbox tags, `workflows` for Wealthbox workflows, `update_contact` for Redtail category). Note/task/tag actions
// report their kind directly in `actionType` (Salesforce-family label assignments come back as
// `tag`). The connector matches the integration service id, so every Salesforce-family overlay
// resolves the same way.
const CRM_ACTION_KIND_BY_CONNECTOR_AND_TYPE: Record<string, CrmActionKind> = {
  'wealthbox.set_tags': 'wealthbox-tag',
  'wealthbox.note': 'wealthbox-note',
  'wealthbox.task': 'wealthbox-task',
  'wealthbox.workflows': 'wealthbox-workflow',
  'redtail.note': 'redtail-note',
  'redtail.task': 'redtail-task',
  'redtail.update_contact': 'redtail-category',
  'advisorengine.note': 'advisorengine-note',
  'advisorengine.task': 'advisorengine-task',
  ...SALESFORCE_FAMILY_SERVICE_IDS.reduce(
    (acc, serviceId) => {
      acc[`${serviceId}.note`] = `${serviceId}-note`
      acc[`${serviceId}.task`] = `${serviceId}-task`
      acc[`${serviceId}.tag`] = `${serviceId}-UserDefinedLabelAssignment`
      return acc
    },
    {} as Record<string, CrmActionKind>,
  ),
}

export const resolveCrmActionKind = (action: CrossClientAction): CrmActionKind | null => {
  const connector = action.details?.connector
  if (typeof connector !== 'string') {
    return null
  }
  const entityType = action.details?.entity_type
  const discriminator = action.actionType === 'entity' && typeof entityType === 'string' ? entityType : action.actionType
  return CRM_ACTION_KIND_BY_CONNECTOR_AND_TYPE[`${connector}.${discriminator}`] ?? null
}
